import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CopyLink, Stack, Typography, closeDialog, showToast } from '@src/components';
import { santaClient } from '@src/state/client';
import { sameOriginUrl } from '@src/routing/urls';
import { EmailInviteForm, emailInviteError, emailInviteSendError } from './EmailInviteForm';

/** Shared by new invitations and reopening an unclaimed organizer's card. */
export function OrganizerInviteShareForm({
  boxKey,
  organizer,
  initialLink,
}: {
  boxKey: string;
  organizer: { id: number; username: string };
  initialLink?: string;
}) {
  const { t } = useTranslation();
  const [link, setLink] = useState(initialLink ?? '');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (initialLink) return;
    let active = true;
    void santaClient
      .createCardAccessLink({ key: boxKey, id: organizer.id })
      .then((response) => {
        if (!active) return;
        if (response.ok && response.data?.url) setLink(sameOriginUrl(response.data.url));
        else setError(t('organizersPanel.saveFailed'));
      })
      .catch(() => {
        if (active) setError(t('organizersPanel.saveFailed'));
      });
    return () => {
      active = false;
    };
  }, [boxKey, organizer.id, initialLink, t]);
  async function sendEmail() {
    if (!link || !consent || emailInviteError(email, t) || busy) return;
    setBusy(true);
    setError(null);
    try {
      const response = await santaClient.sendCardLink({
        key: boxKey,
        id: organizer.id,
        email: email.trim(),
      });
      if (!response.ok) {
        setError(emailInviteSendError(response.error, t));
        return;
      }
      showToast(t('organizersPanel.emailSent'), 'success');
      closeDialog();
    } catch {
      setError(t('organizersPanel.sendFailed'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Stack gap={5}>
      <Typography as="p" role="body/base">
        {t('organizersPanel.shareHint', { name: organizer.username })}
      </Typography>
      <CopyLink
        label={t('organizersPanel.linkLabel')}
        link={link}
        disabled={!link}
        copyHint={t('box.invite.linkCopyHint')}
        copiedHint={t('box.invite.linkCopiedHint')}
        testId="organizer-invite-link"
      />
      <EmailInviteForm
        intro={t('organizersPanel.emailIntro')}
        email={email}
        onEmailChange={setEmail}
        emailLabel={t('box.organizer.email.label')}
        hint={t('box.organizer.email.hint')}
        consentGiven={consent}
        onConsentChange={setConsent}
        error={error}
        testId="organizer-invite-email"
      />
      <Button
        disabled={!link || !consent || !email.trim() || Boolean(emailInviteError(email, t)) || busy}
        loading={busy}
        onClick={() => void sendEmail()}
        testId="organizer-invite-send"
      >
        {t('box.organizer.email.submit')}
      </Button>
    </Stack>
  );
}
